import { c as _c } from "react/compiler-runtime";
import cardValidator from 'card-validator';
import { getTypeInfo } from 'credit-card-type';
import { createContext, useContext, useEffect } from 'react';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { BillingFieldsCardEnum } from '@sola/schema/dotcom/checkout/billingInfo.ts';
import { isEncryptedCardMask } from '@sola/shared-utils/credit-cards.ts';
import { FieldOutput } from '../field/field.tsx';

/**
 * Replicating card-validator's card type interface here
 * since Braintree doesn't have great type support for this
 * and card-validator uses a slightly different interface than credit-card-type
 */
export interface CreditCardType {
  niceType: string;
  type: string;
  patterns: Array<number[] | number>;
  gaps: number[];
  lengths: number[];
  code: {
    name: string;
    size: number;
  };
  hasEncryptedMask?: boolean;
}

/**
 * Replicating card validator's functionality.
 * We may need to build off this in the future.
 */
export const creditCardValidator: typeof cardValidator = {
  ...cardValidator
};

/**
 * Data being passed within credit card context.
 */
interface CardContextData {
  card: CreditCardType | null;
  validator: typeof cardValidator;
  setCard: (card: CreditCardType | null) => void;
}

/**
 * Credit card context.
 */
export const CreditCardContext = createContext<CardContextData | null>(null);

/**
 * Hook to consume credit card context.
 */
export const useCreditCardFields = () => {
  const currentContext = useContext(CreditCardContext);
  if (!currentContext) {
    throw new Error("useCreditCardFields must be used within <CreditCardFields>");
  }
  return currentContext;
};

/**
 * Hook to update credit card sate whenever the number field is populated
 * from browser autofill or redis cache.
 *
 * @param fieldName     Name of card number field that needs to be updated.
 * @param formMethods   Form methods for current form.
 */
export const useAutofilledCardNumber = (fieldName, formMethods) => {
  const $ = _c(8);
  const {
    setCard,
    validator
  } = useCreditCardFields();
  const autofilledCardNumber = formMethods?.formState.defaultValues?.[BillingFieldsCardEnum.CardNumber];
  let t0;
  if ($[0] !== autofilledCardNumber || $[1] !== fieldName || $[2] !== formMethods || $[3] !== setCard || $[4] !== validator) {
    t0 = () => {
      if (!autofilledCardNumber) {
        return;
      }
      formMethods?.setValue(fieldName, autofilledCardNumber);
      if (isEncryptedCardMask(autofilledCardNumber)) {
        const type = formMethods?.formState.defaultValues.cardType;
        setCard({
          ...getTypeInfo(type),
          hasEncryptedMask: true
        });
        return;
      }
      setCard(validator.number(autofilledCardNumber).card);
    };
    $[0] = autofilledCardNumber;
    $[1] = fieldName;
    $[2] = formMethods;
    $[3] = setCard;
    $[4] = validator;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  let t1;
  if ($[6] !== autofilledCardNumber) {
    t1 = [autofilledCardNumber];
    $[6] = autofilledCardNumber;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  useEffect(t0, t1);
};

/**
@description Credit card number and security code are tied together in the token - changing one requires re-tokenizing and therefore both values
*/
export const resetCCNumberAndCode = (formContext: UseFormReturn<FieldValues, unknown, unknown>) => {
  formContext.setValue(BillingFieldsCardEnum.CardNumber, null);
  formContext.setValue(BillingFieldsCardEnum.CardCode, null);
};